/**
 * FC001P-50 — Trade Hub recipe builder + recipe library.
 *
 * Two small controllers sharing one sessionStorage collection, following the
 * same pattern as the menu builder (FC001P-45): no backend exists for a
 * per-venue recipe library yet, so "your recipes" is whatever this browser
 * has saved in sessionStorage under one key. RecipeLibrary reads that
 * collection to render cards; RecipeBuilder reads/writes a single recipe in
 * it, addressed by id via a `recipe` query param.
 */
(function () {
  'use strict';

  function genId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'rb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function findById(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(id)) return list[i];
    }
    return null;
  }

  function readJsonPayload(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  /** Matches Liquid's `handleize` closely enough to look up the glass icon
      template the section rendered for a given select option's text. */
  function handleize(value) {
    return String(value || '').toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function splitList(value) {
    return String(value || '')
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(function (v) { return v !== ''; });
  }

  function queryParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  /**
   * Quantities are stored per-serve so the stepper can rescale every row
   * without a "calculate" step. Displayed to one decimal place with the
   * trailing zero trimmed — enough precision that halving a batch of 3
   * still reads sensibly, without printing "16.666666ml".
   */
  function formatQty(perServe, serves) {
    var value = (Number(perServe) || 0) * (Number(serves) || 1);
    var rounded = Math.round(value * 10) / 10;
    return rounded % 1 === 0 ? String(rounded) : String(rounded.toFixed(1));
  }

  /**
   * Published recipes carry ingredients as one string per the live recipe
   * page's own convention ("50ml Vodka", "25ml Funkin Pro-Cocktails Kiwi
   * Purée") — a leading amount, an optional unit stuck directly to it, then
   * the name. sc-recipe-main.liquid only ever needs to split off the amount
   * to show it separately; seeding a builder draft needs the unit split out
   * too, since every row here has its own editable unit dropdown. Falls back
   * to treating the whole string as the name if it doesn't start with a
   * number, rather than guessing at a quantity that was never stated.
   */
  function parseIngredientTitle(title, defaultUnit) {
    var text = String(title || '').trim();
    var match = text.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]*)\s*(.*)$/);
    if (!match || !match[1]) {
      return { amount: null, unit: defaultUnit, name: text };
    }
    return {
      amount: parseFloat(match[1]),
      unit: match[2] || defaultUnit,
      name: match[3] || text
    };
  }

  /* ======================================================================
     RecipeBuilder — a single recipe: info row, ingredients, method, export.
     ====================================================================== */

  function RecipeBuilder(root) {
    this.root = root;
    this.storageKey = root.dataset.storageKey || 'th-recipe-builder';
    this.maxServes = parseInt(root.dataset.maxServes, 10) || 50;
    this.defaultQty = parseFloat(root.dataset.defaultQty) || 25;
    this.defaultUnit = root.dataset.defaultUnit || 'ml';
    this.unitOptions = splitList(root.dataset.unitOptions) || ['ml'];
    this.difficultyOptions = splitList(root.dataset.difficultyOptions) || ['Easy'];
    this.glassOptions = splitList(root.dataset.glassOptions) || ['Highball'];
    this.libraryUrl = root.dataset.libraryUrl || '/pages/my-recipes';

    this.products = readJsonPayload(root, '[data-rb-products]');
    this.ingredientCatalogue = readJsonPayload(root, '[data-rb-ingredients]');
    this.cocktails = readJsonPayload(root, '[data-rb-cocktails]');
    this.categories = this.readCategories();

    this.picker = { open: false, mode: null, category: 'all' };

    this.recipeId = queryParam('recipe');
    this.cocktailHandle = queryParam('cocktail');
    this.state = this.load(this.recipeId)
      || (this.cocktailHandle && this.seedFromCocktail(this.cocktailHandle))
      || this.blankState(this.recipeId);

    this.cacheNodes();
    this.bind();
    this.renderAll();
  }

  RecipeBuilder.prototype.blankState = function (id) {
    return {
      id: id || null,
      name: '',
      image: '',
      serves: 1,
      difficulty: this.difficultyOptions[0] || 'Easy',
      glass: this.glassOptions[0] || 'Highball',
      ingredients: [],
      method: [],
      updatedAt: null
    };
  };

  /**
   * Starting a NEW draft (never id: recipeId — that would make this recipe
   * look like a saved one that just happens to match a cocktail's content,
   * and overwrite it on first save) pre-filled from a published cocktail's
   * real ingredients/method, per FC001P-50: only "New recipe" should ever
   * hand back a genuinely blank page.
   */
  RecipeBuilder.prototype.seedFromCocktail = function (handle) {
    var cocktail = findById(this.cocktails.map(function (c) { return { id: c.handle, data: c }; }), handle);
    if (!cocktail) return null;
    var source = cocktail.data;
    var self = this;

    var state = this.blankState(null);
    state.name = source.name || '';
    state.image = source.image || '';

    (source.ingredients || []).forEach(function (ingredient) {
      var parsed = parseIngredientTitle(ingredient.title, self.defaultUnit);
      state.ingredients.push({
        id: genId(),
        source: 'recipe',
        refId: null,
        name: parsed.name,
        image: '',
        amountPerServe: parsed.amount === null ? self.defaultQty : parsed.amount,
        unit: parsed.unit
      });
    });

    state.method = (source.method || []).slice();

    var stats = source.stats || {};
    Object.keys(stats).forEach(function (label) {
      var key = label.toLowerCase();
      var value = String(stats[label] || '').trim();
      if (!value) return;

      if (key.indexOf('serve') !== -1) {
        var n = parseInt(value, 10);
        if (!isNaN(n) && n > 0) state.serves = Math.min(n, self.maxServes);
      } else if (key.indexOf('difficult') !== -1) {
        var difficultyMatch = self.difficultyOptions.filter(function (o) {
          return o.toLowerCase() === value.toLowerCase();
        })[0];
        if (difficultyMatch) state.difficulty = difficultyMatch;
      } else if (key.indexOf('glass') !== -1) {
        var glassMatch = self.glassOptions.filter(function (o) {
          return o.toLowerCase() === value.toLowerCase();
        })[0];
        if (glassMatch) state.glass = glassMatch;
      }
    });

    return state;
  };

  RecipeBuilder.prototype.readCategories = function () {
    var seen = {};
    var out = [];
    this.ingredientCatalogue.forEach(function (item) {
      var cat = item.category || 'Other';
      if (!seen[cat]) {
        seen[cat] = true;
        out.push(cat);
      }
    });
    return out;
  };

  /* ------------------------------- storage ------------------------------ */

  RecipeBuilder.prototype.readCollection = function () {
    try {
      var raw = window.sessionStorage.getItem(this.storageKey);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  RecipeBuilder.prototype.writeCollection = function (list) {
    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(list));
      return true;
    } catch (e) {
      return false;
    }
  };

  RecipeBuilder.prototype.load = function (id) {
    if (!id) return null;
    var found = findById(this.readCollection(), id);
    if (!found) return null;
    var base = this.blankState(id);
    Object.keys(base).forEach(function (key) {
      if (found[key] !== undefined) base[key] = found[key];
    });
    if (!Array.isArray(base.ingredients)) base.ingredients = [];
    if (!Array.isArray(base.method)) base.method = [];
    return base;
  };

  RecipeBuilder.prototype.save = function () {
    if (!this.state.id) this.state.id = genId();
    this.state.updatedAt = new Date().toISOString();

    var list = this.readCollection();
    var index = -1;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(this.state.id)) { index = i; break; }
    }
    if (index === -1) list.push(this.state);
    else list[index] = this.state;

    var ok = this.writeCollection(list);

    /* The id is only in the URL after a save, so reloading or sharing the
       link during an unsaved "new recipe" session doesn't silently start
       overwriting a different draft. */
    if (ok && queryParam('recipe') !== String(this.state.id)) {
      var url = new URL(window.location.href);
      url.searchParams.set('recipe', this.state.id);
      window.history.replaceState({}, '', url);
    }
    return ok;
  };

  /* -------------------------------- nodes -------------------------------- */

  RecipeBuilder.prototype.cacheNodes = function () {
    var r = this.root;
    this.nodes = {
      name: r.querySelector('[data-rb-name]'),
      serves: r.querySelector('[data-rb-serves]'),
      servesDown: r.querySelector('[data-rb-serves-down]'),
      servesUp: r.querySelector('[data-rb-serves-up]'),
      difficulty: r.querySelector('[data-rb-difficulty]'),
      glass: r.querySelector('[data-rb-glass]'),
      glassIcon: r.querySelector('[data-rb-glass-icon]'),
      tabs: r.querySelectorAll('[data-rb-tab]'),
      panelIngredients: r.querySelector('[data-rb-panel="ingredients"]'),
      panelMethod: r.querySelector('[data-rb-panel="method"]'),
      rows: r.querySelector('[data-rb-rows]'),
      empty: r.querySelector('[data-rb-empty]'),
      sourceToggle: r.querySelector('[data-rb-source-toggle]'),
      browseBtn: r.querySelector('[data-rb-source="funkin"]'),
      otherBtn: r.querySelector('[data-rb-source="other"]'),
      picker: r.querySelector('[data-rb-picker]'),
      steps: r.querySelector('[data-rb-steps]'),
      addStep: r.querySelector('[data-rb-add-step]'),
      save: r.querySelector('[data-rb-save]'),
      export: r.querySelector('[data-rb-export]'),
      status: r.querySelector('[data-rb-status]'),
      exportSuccess: r.querySelector('[data-rb-export-success]'),
      exportMeta: r.querySelector('[data-rb-export-meta]'),
      exportDownload: r.querySelector('[data-rb-export-download]'),
      exportFailure: r.querySelector('[data-rb-export-failure]')
    };
  };

  RecipeBuilder.prototype.bind = function () {
    var self = this;

    if (this.nodes.name) {
      this.nodes.name.addEventListener('input', function () {
        self.state.name = this.value;
        self.save();
      });
    }

    if (this.nodes.servesDown) {
      this.nodes.servesDown.addEventListener('click', function () { self.stepServes(-1); });
    }
    if (this.nodes.servesUp) {
      this.nodes.servesUp.addEventListener('click', function () { self.stepServes(1); });
    }

    if (this.nodes.difficulty) {
      this.nodes.difficulty.addEventListener('change', function () {
        self.state.difficulty = this.value;
        self.save();
      });
    }

    if (this.nodes.glass) {
      this.nodes.glass.addEventListener('change', function () {
        self.state.glass = this.value;
        self.paintGlassIcon();
        self.save();
      });
    }

    Array.prototype.forEach.call(this.nodes.tabs, function (tab) {
      tab.addEventListener('click', function () {
        self.setTab(tab.dataset.rbTab);
      });
    });

    if (this.nodes.browseBtn) {
      this.nodes.browseBtn.addEventListener('click', function () {
        self.togglePicker('funkin');
      });
    }
    if (this.nodes.otherBtn) {
      this.nodes.otherBtn.addEventListener('click', function () {
        self.togglePicker('other');
      });
    }

    if (this.nodes.addStep) {
      this.nodes.addStep.addEventListener('click', function () {
        self.state.method.push('');
        self.renderMethod();
        self.save();
        var last = self.nodes.steps.querySelector('[data-rb-step]:last-child .th-rb__step-input');
        if (last) last.focus();
      });
    }

    if (this.nodes.save) {
      this.nodes.save.addEventListener('click', function () {
        self.save();
        self.announce(self.text('savedText', 'Recipe saved.'));
      });
    }

    if (this.nodes.export) {
      this.nodes.export.addEventListener('click', function () {
        self.exportCard();
      });
    }
  };

  RecipeBuilder.prototype.text = function (key, fallback) {
    var value = this.root.dataset[key];
    return value === undefined || value === '' ? fallback : value;
  };

  RecipeBuilder.prototype.announce = function (message) {
    if (!this.nodes.status) return;
    this.nodes.status.textContent = message;
  };

  /* -------------------------------- serves -------------------------------- */

  RecipeBuilder.prototype.stepServes = function (delta) {
    var next = Math.max(1, Math.min(this.maxServes, (this.state.serves || 1) + delta));
    if (next === this.state.serves) return;
    this.state.serves = next;
    this.paintServes();
    this.updateQuantityDisplays();
    this.save();
  };

  RecipeBuilder.prototype.paintGlassIcon = function () {
    if (!this.nodes.glassIcon) return;
    var tpl = this.root.querySelector(
      '[data-rb-glass-icon-template="' + handleize(this.state.glass) + '"]'
    ) || this.root.querySelector('[data-rb-glass-icon-template="highball"]');
    if (!tpl) return;
    this.nodes.glassIcon.innerHTML = '';
    this.nodes.glassIcon.appendChild(tpl.content.cloneNode(true));
  };

  RecipeBuilder.prototype.paintServes = function () {
    if (this.nodes.serves) this.nodes.serves.textContent = this.state.serves;
    if (this.nodes.servesDown) this.nodes.servesDown.disabled = this.state.serves <= 1;
    if (this.nodes.servesUp) this.nodes.servesUp.disabled = this.state.serves >= this.maxServes;
  };

  /* --------------------------------- tabs ---------------------------------- */

  RecipeBuilder.prototype.setTab = function (tab) {
    Array.prototype.forEach.call(this.nodes.tabs, function (btn) {
      var active = btn.dataset.rbTab === tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', String(active));
    });
    if (this.nodes.panelIngredients) this.nodes.panelIngredients.hidden = tab !== 'ingredients';
    if (this.nodes.panelMethod) this.nodes.panelMethod.hidden = tab !== 'method';
  };

  /* ----------------------------- ingredients ------------------------------ */

  RecipeBuilder.prototype.addIngredient = function (source, item) {
    this.state.ingredients.push({
      id: genId(),
      source: source,
      refId: item.id !== undefined ? String(item.id) : null,
      name: item.name,
      image: item.image || '',
      amountPerServe: this.defaultQty / (this.state.serves || 1),
      unit: this.defaultUnit
    });
    this.renderIngredients();
    this.save();
  };

  RecipeBuilder.prototype.removeIngredient = function (index) {
    this.state.ingredients.splice(index, 1);
    this.renderIngredients();
    this.save();
  };

  RecipeBuilder.prototype.moveIngredient = function (index, delta) {
    var target = index + delta;
    if (target < 0 || target >= this.state.ingredients.length) return;
    var list = this.state.ingredients;
    var item = list[index];
    list.splice(index, 1);
    list.splice(target, 0, item);
    this.renderIngredients();
    this.save();
  };

  RecipeBuilder.prototype.renderIngredients = function () {
    var self = this;
    var host = this.nodes.rows;
    if (!host) return;
    host.innerHTML = '';

    var has = this.state.ingredients.length > 0;
    if (this.nodes.empty) this.nodes.empty.hidden = has;
    host.hidden = !has;
    if (!has) return;

    this.state.ingredients.forEach(function (ingredient, index) {
      host.appendChild(self.buildIngredientRow(ingredient, index));
    });
  };

  RecipeBuilder.prototype.buildIngredientRow = function (ingredient, index) {
    var self = this;
    var total = this.state.ingredients.length;

    var li = document.createElement('li');
    li.className = 'th-rb__row';
    li.dataset.rbRow = String(index);

    if (ingredient.source === 'funkin') {
      var badge = document.createElement('span');
      badge.className = 'th-rb__row-badge';
      if (ingredient.image) {
        var img = document.createElement('img');
        img.src = ingredient.image;
        img.alt = '';
        badge.appendChild(img);
      } else {
        badge.textContent = 'FUNKIN';
      }
      li.appendChild(badge);
    }

    var name = document.createElement('span');
    name.className = 'th-rb__row-name';
    name.textContent = ingredient.name;
    li.appendChild(name);

    var qty = document.createElement('input');
    qty.type = 'text';
    qty.inputMode = 'decimal';
    qty.className = 'th-rb__row-qty';
    qty.setAttribute('aria-label', 'Quantity for ' + ingredient.name);
    qty.value = formatQty(ingredient.amountPerServe, this.state.serves);
    qty.addEventListener('input', function () {
      var n = parseFloat(this.value.replace(',', '.'));
      if (!isNaN(n)) {
        ingredient.amountPerServe = n / (self.state.serves || 1);
        self.save();
      }
    });
    qty.addEventListener('blur', function () {
      this.value = formatQty(ingredient.amountPerServe, self.state.serves);
    });
    li.appendChild(qty);

    var unit = document.createElement('select');
    unit.className = 'th-rb__row-unit';
    unit.setAttribute('aria-label', 'Unit for ' + ingredient.name);
    this.unitOptions.forEach(function (u) {
      var opt = document.createElement('option');
      opt.value = u;
      opt.textContent = u;
      if (u === ingredient.unit) opt.selected = true;
      unit.appendChild(opt);
    });
    unit.addEventListener('change', function () {
      ingredient.unit = this.value;
      self.save();
    });
    li.appendChild(unit);

    var reorder = document.createElement('span');
    reorder.className = 'th-rb__row-reorder';

    var up = document.createElement('button');
    up.type = 'button';
    up.setAttribute('aria-label', 'Move ' + ingredient.name + ' up');
    up.disabled = index === 0;

    var down = document.createElement('button');
    down.type = 'button';
    down.setAttribute('aria-label', 'Move ' + ingredient.name + ' down');
    down.disabled = index === total - 1;

    /* Chevron markup is injected from the section's own template (see the
       [data-rb-chevron-template] <template> in sc-recipe-builder.liquid) so
       this file doesn't have to duplicate the icon's SVG path data. */
    var chevronTpl = self.root.querySelector('[data-rb-chevron-template]');
    if (chevronTpl) {
      var upIcon = chevronTpl.content.cloneNode(true);
      upIcon.querySelector('svg').classList.add('is-up');
      up.appendChild(upIcon);
      down.appendChild(chevronTpl.content.cloneNode(true));
    }

    up.addEventListener('click', function () { self.moveIngredient(index, -1); });
    down.addEventListener('click', function () { self.moveIngredient(index, 1); });
    reorder.appendChild(up);
    reorder.appendChild(down);
    li.appendChild(reorder);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'th-rb__row-remove';
    remove.setAttribute('aria-label', 'Remove ' + ingredient.name);
    var closeTpl = self.root.querySelector('[data-rb-close-template]');
    if (closeTpl) remove.appendChild(closeTpl.content.cloneNode(true));
    remove.addEventListener('click', function () { self.removeIngredient(index); });
    li.appendChild(remove);

    return li;
  };

  /** After a serves change every row's displayed quantity needs updating,
      but rebuilding the list would drop focus from whatever the customer
      was just typing into — so values are painted onto the existing inputs
      instead of calling renderIngredients(). */
  RecipeBuilder.prototype.updateQuantityDisplays = function () {
    var self = this;
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-rb-row]'), function (row) {
      var index = parseInt(row.dataset.rbRow, 10);
      var ingredient = self.state.ingredients[index];
      var input = row.querySelector('.th-rb__row-qty');
      if (ingredient && input && document.activeElement !== input) {
        input.value = formatQty(ingredient.amountPerServe, self.state.serves);
      }
    });
  };

  /* -------------------------------- method --------------------------------- */

  RecipeBuilder.prototype.removeStep = function (index) {
    this.state.method.splice(index, 1);
    this.renderMethod();
    this.save();
  };

  RecipeBuilder.prototype.renderMethod = function () {
    var self = this;
    var host = this.nodes.steps;
    if (!host) return;
    host.innerHTML = '';

    this.state.method.forEach(function (step, index) {
      var li = document.createElement('li');
      li.className = 'th-rb__step';
      li.dataset.rbStep = String(index);

      var badge = document.createElement('span');
      badge.className = 'th-rb__step-badge';
      badge.textContent = String(index + 1);
      li.appendChild(badge);

      var input = document.createElement('textarea');
      input.className = 'th-rb__step-input';
      input.rows = 1;
      input.value = step;
      input.setAttribute('aria-label', 'Step ' + (index + 1));
      input.addEventListener('input', function () {
        self.state.method[index] = this.value;
        self.save();
      });
      li.appendChild(input);

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'th-rb__step-remove';
      remove.setAttribute('aria-label', 'Remove step ' + (index + 1));
      var closeTpl = self.root.querySelector('[data-rb-close-template]');
      if (closeTpl) remove.appendChild(closeTpl.content.cloneNode(true));
      remove.addEventListener('click', function () { self.removeStep(index); });
      li.appendChild(remove);

      host.appendChild(li);
    });
  };

  /* -------------------------------- picker --------------------------------- */

  RecipeBuilder.prototype.togglePicker = function (mode) {
    if (this.picker.open && this.picker.mode === mode) {
      this.picker.open = false;
    } else {
      this.picker.open = true;
      this.picker.mode = mode;
      this.picker.category = 'all';
    }
    this.paintPicker();
  };

  RecipeBuilder.prototype.paintPicker = function () {
    if (this.nodes.browseBtn) {
      this.nodes.browseBtn.classList.toggle('is-active', this.picker.open && this.picker.mode === 'funkin');
    }
    if (this.nodes.otherBtn) {
      this.nodes.otherBtn.classList.toggle('is-active', this.picker.open && this.picker.mode === 'other');
    }
    if (!this.nodes.picker) return;

    this.nodes.picker.hidden = !this.picker.open;
    if (!this.picker.open) return;

    this.nodes.picker.innerHTML = '';
    if (this.picker.mode === 'funkin') {
      this.nodes.picker.appendChild(this.buildFunkinPanel());
    } else {
      this.nodes.picker.appendChild(this.buildOtherPanel());
    }
  };

  RecipeBuilder.prototype.buildFunkinPanel = function () {
    var self = this;
    var wrap = document.createDocumentFragment();

    var search = document.createElement('input');
    search.type = 'search';
    search.className = 'th-rb__panel-search';
    search.placeholder = this.text('funkinSearchPlaceholder', 'Search Funkin products');
    wrap.appendChild(search);

    var list = document.createElement('ul');
    list.className = 'th-rb__panel-list';
    wrap.appendChild(list);

    function paint(term) {
      var needle = (term || '').trim().toLowerCase();
      list.innerHTML = '';

      if (!self.products.length) {
        list.appendChild(self.panelEmptyRow(self.text('noProductsText', 'No Funkin products are available yet.')));
        return;
      }

      var matches = self.products.filter(function (p) {
        return !needle || p.name.toLowerCase().indexOf(needle) !== -1;
      });

      if (!matches.length) {
        list.appendChild(self.panelEmptyRow('Nothing matches “' + term + '”.'));
        return;
      }

      matches.forEach(function (product) {
        list.appendChild(self.buildAddChip(product.name, function () {
          self.addIngredient('funkin', product);
        }));
      });
    }

    search.addEventListener('input', function () { paint(this.value); });
    paint('');

    return wrap;
  };

  RecipeBuilder.prototype.buildOtherPanel = function () {
    var self = this;
    var wrap = document.createDocumentFragment();

    var addRow = document.createElement('div');
    addRow.className = 'th-rb__panel-add';
    var addInput = document.createElement('input');
    addInput.type = 'text';
    addInput.className = 'th-rb__panel-search';
    addInput.placeholder = this.text('otherSearchPlaceholder', 'Search or type an ingredient and press enter');
    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'th-rb__btn th-rb__btn--secondary th-rb__btn--small';
    addBtn.textContent = this.text('addLabel', 'Add');

    function addFreeText() {
      var value = addInput.value.trim();
      if (!value) return;
      self.addIngredient('other', { name: value });
      addInput.value = '';
      paintList();
    }

    addInput.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        addFreeText();
      }
    });
    addBtn.addEventListener('click', addFreeText);
    addRow.appendChild(addInput);
    addRow.appendChild(addBtn);
    wrap.appendChild(addRow);

    var popular = this.ingredientCatalogue.filter(function (i) { return i.popular; });
    if (popular.length) {
      var popularLabel = document.createElement('span');
      popularLabel.className = 'th-rb__panel-label th-s3';
      popularLabel.textContent = this.text('popularLabel', 'Popular to start with');
      wrap.appendChild(popularLabel);

      var popularList = document.createElement('ul');
      popularList.className = 'th-rb__panel-list';
      popular.forEach(function (item) {
        popularList.appendChild(self.buildAddChip(item.name, function () {
          self.addIngredient('other', item);
        }));
      });
      wrap.appendChild(popularList);
    }

    if (this.categories.length > 1) {
      var catLabel = document.createElement('span');
      catLabel.className = 'th-rb__panel-label th-s3';
      catLabel.textContent = this.text('categoriesLabel', 'Categories');
      wrap.appendChild(catLabel);

      var cats = document.createElement('div');
      cats.className = 'th-rb__panel-cats';
      this.categories.forEach(function (cat) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'th-rb__panel-cat' + (self.picker.category === cat ? ' is-active' : '');
        btn.textContent = cat;
        btn.addEventListener('click', function () {
          self.picker.category = self.picker.category === cat ? 'all' : cat;
          paintList();
          Array.prototype.forEach.call(cats.children, function (b) {
            b.classList.toggle('is-active', b === btn && self.picker.category === cat);
          });
        });
        cats.appendChild(btn);
      });
      wrap.appendChild(cats);
    }

    var list = document.createElement('ul');
    list.className = 'th-rb__panel-list';
    wrap.appendChild(list);

    function paintList() {
      list.innerHTML = '';
      var items = self.ingredientCatalogue.filter(function (item) {
        return self.picker.category === 'all' || item.category === self.picker.category;
      });
      if (!items.length) {
        list.appendChild(self.panelEmptyRow(self.text('noIngredientsText', 'No ingredients have been set up in this category yet.')));
        return;
      }
      items.forEach(function (item) {
        list.appendChild(self.buildAddChip(item.name, function () {
          self.addIngredient('other', item);
        }));
      });
    }
    paintList();

    return wrap;
  };

  RecipeBuilder.prototype.buildAddChip = function (label, onAdd) {
    var li = document.createElement('li');
    var chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'th-rb__chip';
    var plusTpl = this.root.querySelector('[data-rb-plus-template]');
    if (plusTpl) chip.appendChild(plusTpl.content.cloneNode(true));
    var text = document.createElement('span');
    text.textContent = label;
    chip.appendChild(text);
    chip.addEventListener('click', onAdd);
    li.appendChild(chip);
    return li;
  };

  RecipeBuilder.prototype.panelEmptyRow = function (message) {
    var li = document.createElement('li');
    li.className = 'th-rb__panel-empty';
    li.textContent = message;
    return li;
  };

  /* -------------------------------- export ---------------------------------- */

  /** What the card shows: quantities already scaled to the current serves,
      not the stored per-serve amounts, and no blank method steps. */
  RecipeBuilder.prototype.cardData = function () {
    var self = this;
    return {
      name: this.state.name,
      image: this.state.image || '',
      serves: this.state.serves,
      difficulty: this.state.difficulty,
      glass: this.state.glass,
      ingredients: this.state.ingredients.map(function (ingredient) {
        return {
          quantity: formatQty(ingredient.amountPerServe, self.state.serves),
          unit: ingredient.unit,
          name: ingredient.name
        };
      }),
      method: this.state.method.filter(function (step) {
        return String(step).trim() !== '';
      }),
      labels: {
        eyebrow: this.text('cardEyebrow', 'Funkin cocktail recipe'),
        serves: this.text('servesLabel', 'Serves'),
        difficulty: this.text('difficultyLabel', 'Difficulty'),
        glass: this.text('glassLabel', 'Glass'),
        ingredients: this.text('ingredientsLabel', 'Ingredients'),
        method: this.text('methodLabel', 'Method'),
        untitled: this.text('untitledRecipe', 'Untitled recipe')
      }
    };
  };

  /** Drawn in the browser and offered as a download, the same way the asset
      creator does it (see tradehub-recipe-card.js). Nothing is sent
      anywhere, so there is no print service involved. */
  RecipeBuilder.prototype.exportCard = function () {
    var self = this;
    this.save();

    if (this.nodes.exportSuccess) this.nodes.exportSuccess.hidden = true;
    if (this.nodes.exportDownload) this.nodes.exportDownload.hidden = true;
    if (this.nodes.exportFailure) this.nodes.exportFailure.hidden = true;

    /* Guard against a double tap rendering the card twice. */
    if (this.exporting) return;
    this.exporting = true;
    if (this.nodes.export) this.nodes.export.disabled = true;

    var renderer = window.ThRecipeCard && window.ThRecipeCard.render;
    var job = renderer
      ? renderer(this.cardData())
      : Promise.reject(new Error('Recipe card renderer is not loaded'));

    job.then(function (result) {
      self.finishExport(result);
    }).catch(function (error) {
      /* eslint-disable-next-line no-console */
      console.error('[recipe-builder] card render failed', error);
      if (self.nodes.exportFailure) self.nodes.exportFailure.hidden = false;
    }).then(function () {
      self.exporting = false;
      if (self.nodes.export) self.nodes.export.disabled = false;
    });
  };

  RecipeBuilder.prototype.finishExport = function (result) {
    if (this.activeObjectUrl) URL.revokeObjectURL(this.activeObjectUrl);
    this.activeObjectUrl = URL.createObjectURL(result.blob);

    var slug = handleize(this.state.name) || 'recipe';

    if (this.nodes.exportDownload) {
      this.nodes.exportDownload.href = this.activeObjectUrl;
      this.nodes.exportDownload.setAttribute('download', slug + '-recipe-card.png');
      this.nodes.exportDownload.hidden = false;
    }

    if (this.nodes.exportMeta) {
      var kb = Math.max(1, Math.round(result.blob.size / 1024));
      this.nodes.exportMeta.textContent =
        'PNG · ' + result.width + ' × ' + result.height + 'px · ' + kb + ' KB';
    }

    if (this.nodes.exportSuccess) {
      this.nodes.exportSuccess.hidden = false;
      this.nodes.exportSuccess.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  /* --------------------------------- render ----------------------------------- */

  RecipeBuilder.prototype.renderAll = function () {
    if (this.nodes.name) this.nodes.name.value = this.state.name;
    if (this.nodes.difficulty) this.nodes.difficulty.value = this.state.difficulty;
    if (this.nodes.glass) this.nodes.glass.value = this.state.glass;
    this.paintGlassIcon();
    this.paintServes();
    this.setTab('ingredients');
    this.renderIngredients();
    this.renderMethod();
    this.paintPicker();
  };

  /* ======================================================================
     RecipeLibrary — "your recipes" browse page.
     ====================================================================== */

  function RecipeLibrary(root) {
    this.root = root;
    this.storageKey = root.dataset.storageKey || 'th-recipe-builder';
    this.spiritTags = splitList(root.dataset.spiritTags);
    this.editorUrl = root.dataset.editorUrl || '/pages/recipe-editor';
    this.activeFilter = 'all';

    this.nodes = {
      filters: root.querySelector('[data-rb-filters]'),
      cards: root.querySelector('[data-rb-cards]'),
      empty: root.querySelector('[data-rb-lib-empty]')
    };

    this.recipes = this.readCollection();
    this.cocktails = readJsonPayload(root, '[data-rb-cocktails]');
    this.items = this.buildItems();
    this.renderFilters();
    this.renderCards();
  }

  RecipeLibrary.prototype.readCollection = function () {
    try {
      var raw = window.sessionStorage.getItem(this.storageKey);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  /** One card shape for two very different sources: a saved draft (this
      browser's sessionStorage) and a published cocktail (the recipe blog,
      same data the menu builder reads). Saved drafts are listed first —
      they're what this venue actually built — with the full catalogue
      after, so the grid is never empty before anyone's built anything. */
  RecipeLibrary.prototype.buildItems = function () {
    var savedItems = this.recipes.map(function (recipe) {
      return {
        kind: 'saved',
        id: recipe.id,
        name: recipe.name,
        image: '',
        serves: recipe.serves || 1,
        ingredientNames: (recipe.ingredients || []).map(function (i) { return i.name; })
      };
    });

    var catalogueItems = this.cocktails.map(function (cocktail) {
      var serves = null;
      var stats = cocktail.stats || {};
      Object.keys(stats).forEach(function (label) {
        if (label.toLowerCase().indexOf('serve') !== -1) {
          var n = parseInt(stats[label], 10);
          if (!isNaN(n) && n > 0) serves = n;
        }
      });
      return {
        kind: 'catalogue',
        handle: cocktail.handle,
        name: cocktail.name,
        image: cocktail.image || '',
        serves: serves,
        ingredientNames: (cocktail.ingredients || []).map(function (i) { return i.title; })
      };
    });

    return savedItems.concat(catalogueItems);
  };

  /** No merchant-defined taxonomy exists for "what spirit is this recipe",
      so an item is tagged by matching its ingredient names against the
      configured quick-add spirit chips — good enough to drive the library's
      filter chips without inventing a new settings field for it. */
  RecipeLibrary.prototype.tagsFor = function (item) {
    var names = (item.ingredientNames || []).join(' | ').toLowerCase();
    return this.spiritTags.filter(function (tag) {
      return names.indexOf(tag.toLowerCase()) !== -1;
    });
  };

  RecipeLibrary.prototype.renderFilters = function () {
    var self = this;
    var host = this.nodes.filters;
    if (!host) return;

    var present = {};
    this.items.forEach(function (item) {
      self.tagsFor(item).forEach(function (tag) { present[tag] = true; });
    });
    var tags = this.spiritTags.filter(function (tag) { return present[tag]; });

    host.innerHTML = '';
    host.appendChild(this.buildFilterChip('All', 'all'));
    tags.forEach(function (tag) {
      host.appendChild(self.buildFilterChip(tag, tag));
    });
  };

  RecipeLibrary.prototype.buildFilterChip = function (label, value) {
    var self = this;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'th-rb__chip' + (this.activeFilter === value ? ' is-active' : '');
    btn.textContent = label;
    btn.addEventListener('click', function () {
      self.activeFilter = value;
      Array.prototype.forEach.call(self.nodes.filters.children, function (chip) {
        chip.classList.toggle('is-active', chip === btn);
      });
      self.renderCards();
    });
    return btn;
  };

  RecipeLibrary.prototype.renderCards = function () {
    var self = this;
    var host = this.nodes.cards;
    if (!host) return;

    var filtered = this.items.filter(function (item) {
      return self.activeFilter === 'all' || self.tagsFor(item).indexOf(self.activeFilter) !== -1;
    });

    host.innerHTML = '';
    host.hidden = filtered.length === 0;
    if (this.nodes.empty) this.nodes.empty.hidden = filtered.length !== 0;

    filtered.forEach(function (item) {
      host.appendChild(self.buildCard(item));
    });
  };

  RecipeLibrary.prototype.buildCard = function (item) {
    var card = document.createElement('div');
    card.className = 'th-rb__card';

    var thumb = document.createElement('div');
    thumb.className = 'th-rb__card-thumb';
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    } else {
      thumb.textContent = this.root.dataset.previewText || 'Recipe preview';
    }
    card.appendChild(thumb);

    var body = document.createElement('div');
    body.className = 'th-rb__card-body';

    var title = document.createElement('h3');
    title.className = 'th-rb__card-title th-h5';
    title.textContent = item.name || this.root.dataset.untitledText || 'Untitled recipe';
    body.appendChild(title);

    var tags = this.tagsFor(item);
    var meta = document.createElement('p');
    meta.className = 'th-rb__card-meta th-s3';
    var metaParts = [];
    if (tags.length) metaParts.push(tags[0].toUpperCase());
    if (item.serves) metaParts.push('SERVES ' + item.serves);
    if (item.kind === 'catalogue' && !metaParts.length) metaParts.push('FUNKIN RECIPE');
    meta.textContent = metaParts.join(' · ');
    body.appendChild(meta);

    var open = document.createElement('a');
    open.className = 'th-rb__btn th-rb__btn--secondary th-rb__btn--small th-rb__card-open';
    if (item.kind === 'saved') {
      open.href = this.editorUrl + '?recipe=' + encodeURIComponent(item.id);
      open.textContent = this.root.dataset.openText || 'Open recipe';
    } else {
      open.href = this.editorUrl + '?cocktail=' + encodeURIComponent(item.handle);
      open.textContent = this.root.dataset.useText || 'Use this recipe';
    }
    body.appendChild(open);

    card.appendChild(body);
    return card;
  };

  /* ---------------------------------- init ------------------------------------ */

  document.addEventListener('DOMContentLoaded', function () {
    var builderRoot = document.querySelector('[data-recipe-builder]');
    if (builderRoot) new RecipeBuilder(builderRoot);

    var libraryRoot = document.querySelector('[data-recipe-library]');
    if (libraryRoot) new RecipeLibrary(libraryRoot);
  });
})();
